<script setup>
import {defineProps, onMounted, ref, watch} from "vue";
import {
  useFormatMoney,
  useFormatDayMonth,
  useFormatTime,
} from "@/composables/format";
import { useUserStore } from "@/stores/user";
import { useBasketStore } from "@/stores/basket";
import {useBranchesStore} from "@/stores/branches";
import { disableLoggedInPrice } from "@/config/flags";

const userStore = useUserStore();

const formatDate = useFormatDayMonth();

const formatTime = useFormatTime();

const formatMoney = useFormatMoney();
const branchStore = useBranchesStore();
const branchData = ref(null);
const termsData = ref([]);
const loading = ref(false)

const props = defineProps(["terms", "alignRight", 'inHp', 'loading']);

const basketStore = useBasketStore();

onMounted(() => {
  branchData.value = branchStore.selectedBranchData
  termsData.value = props.terms
})

const selectUnselectTerm = (term) => {
  if (basketStore.basket.find((item) => item.id === term.id)) {
    basketStore.removeProduct(term);
  } else {
    basketStore.basket.push(term);
  }
};

const isSelected = (term) => {
  return !!basketStore.basket.find((item) => item.id === term.id);
};
watch(() => props.terms, () => {
  termsData.value = props.terms
})
watch(() => props.loading, () => {
  loading.value = props.loading
})
</script>

<template>
  <div class="term-list" :class="{ withoutBorder: alignRight, hpShow: inHp, availableTerms: !inHp }">
    <div :class="{hpShowHeadline: inHp}">
      <h5 v-if="inHp">{{ $t('terms.list_of_terms') }}</h5>
      <div v-if="loading" class="loader-container">
        <div class="loader"></div>
      </div>
      <ul v-if="terms.length && !loading" :class="{ right: alignRight }">
        <li
          v-for="term in termsData"
          :key="term.id"
          @click="selectUnselectTerm(term)"
          :class="{ isSelected: isSelected(term) }"
        >
          <div class="term-item">
            <div class="term-date">
              {{ formatDate(term.from) }}
              <i class="fa-chevron-right fas" aria-hidden="true"></i>
              {{ formatTime(term.from) }} -
              {{ formatTime(term.to) }}
            </div>
            <div class="term-prices">
              <div v-if="disableLoggedInPrice" class="highlighted">
                <div class="price">{{ formatMoney(term.logged_price) }}</div>
              </div>
              <template v-else>
                <div class="highlighted registered">
                  <div class="price-label">{{ $t('terms.registered') }}</div>
                  <div class="price">{{ formatMoney(term.logged_price) }}</div>
                </div>
                <div v-if="!userStore.loggedIn" class="not-registered">
                  <div class="price-label">{{ $t('terms.not_registered') }}</div>
                  <div class="price">{{ formatMoney(term.price) }}</div>
                </div>
              </template>
            </div>
          </div>
        </li>
      </ul>
      <div class="mt-2" v-else>{{ $t('terms.no_terms_for_this_date') }}</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.term-list {
  margin-top: 30px;
  border-top: 1px solid #d2d2d2;
  margin-bottom: 30px;
  @media screen and (max-width: 50em) {
    margin: 30px 0;
  }
  &.availableTerms {
    margin-bottom: 30px;
    border-top: 0;
    @media screen and (max-width: 800px) {
        width: 100%;
        display: flex;
        flex-basis: 100%;
        flex-wrap: wrap;
        flex-direction: column;
    }
  }

  &.withoutBorder {
    border-top: none;
  }
  &.hpShow {
    margin-top: 0;
    flex-basis: 49%;
    margin-right: 1%;

    ul {
      @media screen and (max-width: 1600px) {
        grid-template-columns: 1fr 1fr;
      }
    }

    &.availableTerms{
      margin-left: 30px;
    }

    @media screen and (max-width: 1000px) {
      flex-basis: 100%;
    }
    .hpShowHeadline {
      text-align: right;
      .branch-select {
        margin-left: 10%;
      }
      h5 {
        margin-bottom: 5px;
      }
    }
  }

  .term-prices {
    display: flex;
    text-align: center;
    justify-content: space-between;

    @media screen and (max-width: 800px) {
      letter-spacing: 0.5px;

      .price-label {
        font-size: 10px !important;
      }
    }

    > div {
      flex-grow: 1;

      &:first-child {
        margin-right: 10px;
      }
    }

    .highlighted {
      color: #c39700;
      font-weight: bold;
    }

    .price-label {
      font-size: 12px;
      text-transform: uppercase;
    }
  }

  ul {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 15px;
    list-style: none;
    padding: 0px;

    @media screen and (max-width: 1200px) {
      grid-template-columns: 1fr 1fr;
    }

    li {
      border-radius: 10px;
      background-color: white;
      padding: 10px 15px;
      box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.2);
      transition: all 0.2s ease-in-out;
      cursor: pointer;
      user-select: none;
      text-align: center;

      @media screen and (max-width: 800px) {
        font-size: 12px;
        flex-basis: 100%;
        max-width: 100%;

        &:nth-child(2n) {
          margin-left: 0px;
          margin-right: 0px;
        }

        .term-prices {
          .registered {
            text-align: left;
          }
          .not-registered {
            text-align: right;
          }
        }
      }

      .term-date {
        font-size: 20px;
        font-weight: bold;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid #dedede;
        margin-bottom: 5px;

        @media screen and (min-width: 800px){
          min-width: 250px;
        }

        .fas {
          font-size: 14px;
          margin: 0px 5px;
        }

        @media screen and (max-width: 800px) {
          font-size: 18px;
        }

        @media screen and (max-width: 600px) {
          font-size: 14px;
        }
      }

      &.isSelected {
        background-color: #a39a7b;
        color: white;

        .highlighted {
          color: white;
        }
      }

      &:hover {
        transform: scale(1.05);
      }

      &:last-of-type {
        margin-right: 0px;
      }
    }

    &.right {
      justify-content: flex-end;
    }
    @media screen and (max-width: 450px) {
      display: flex;
      flex-wrap: wrap;
      width: 97%;
      margin: 10px auto;
    }
  }
}
.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  z-index: 1000;
}

.loader {
  width: 50px;
  height: 50px;
  border: 5px solid #ccc;
  border-top-color: #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
